import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import MainNavigation from '../../components/ui/MainNavigation';
import FooterSection from '../homepage/components/FooterSection';
import FeaturedBrandSection from '../../components/FeaturedBrandSection';
import ComparisonModal from '../../components/ComparisonModal';
import { useState } from 'react';
import { AnimatePresence } from 'framer-motion';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';


const VentilationCategoryPage = () => {
  const [comparisonProducts, setComparisonProducts] = useState([]);
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);

  const handleAddToCompare = (product) => {
    setComparisonProducts((prev) => {
      const exists = prev?.find(p => p?.id === product?.id);
      if (exists) {
        return prev;
      }
      if (prev?.length >= 3) {
        return [...prev?.slice(1), product];
      }
      return [...prev, product];
    });
  };

  const featuredBrandData = {
    brandName: "Zehnder",
    brandLogo: "https://ui-avatars.com/api/?name=Zehnder&background=06b6d4&color=fff&size=80",
    brandDescription: "Premium ventilatie met warmteterugwinning",
    productName: "Zehnder ComfoAir Q600",
    productImage: "/assets/images/Untitled-1768597099743.jpeg",
    productImageAlt: "Zehnder ComfoAir Q600 WTW ventilatie unit met hoogste warmteterugwinning efficiëntie",
    productDescription: "Topklasse WTW-unit met 95% warmteterugwinning en ultra-stille werking. Ideaal voor energiezuinige woningen.",
    productSpecs: [
      { icon: "Wind", label: "Capaciteit", value: "600 m³/h" },
      { icon: "Repeat", label: "WTW", value: "95%" },
      { icon: "Volume2", label: "Geluid", value: "25 dB(A)" },
      { icon: "Shield", label: "Garantie", value: "5 jaar" }
    ],
    productPrice: "€5.500",
    rating: 4.9,
    reviewCount: 267,
    reviews: [
      {
        rating: 5,
        text: "Fantastisch systeem! Altijd frisse lucht zonder warmteverlies. Onze stookkosten zijn flink gedaald.",
        name: "Jeroen Peters",
        location: "Nijmegen",
        avatar: "https://ui-avatars.com/api/?name=Jeroen+P&background=06b6d4&color=fff",
        verified: true
      },
      {
        rating: 5,
        text: "Zeer stil en efficiënt. Geen condensatie meer en veel gezonder binnenklimaat. Top investering!",
        name: "Sandra Willems",
        location: "Tilburg",
        avatar: "https://ui-avatars.com/api/?name=Sandra+W&background=0891b2&color=fff",
        verified: true
      },
      {
        rating: 5,
        text: "Perfect systeem met uitstekende app. Filters zijn makkelijk te vervangen. Zeer tevreden!",
        name: "Marco van Dijk",
        location: "Apeldoorn",
        avatar: "https://ui-avatars.com/api/?name=Marco+vD&background=0e7490&color=fff",
        verified: true
      }
    ],
    colorScheme: "from-cyan-500/10 to-blue-500/10",
    accentColor: "text-cyan-600",
    bgGradient: "from-cyan-50 via-blue-50 to-purple-50"
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const ventilationSystems = [
    {
      id: 1,
      name: "Natuurlijke ventilatie",
      description: "Ventilatie via ramen, roosters en kieren. Eenvoudig maar minder controleerbaar.",
      suitability: "Geschikt voor oudere woningen zonder mechanische systemen",
      attention: "Afhankelijk van wind en temperatuurverschil, kan leiden tot warmteverlies",
      icon: "Wind",
      color: "from-blue-500/10 to-cyan-500/10",
      image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?w=400&h=300&fit=crop",
      alt: "Open raam met natuurlijke ventilatie in moderne woning"
    },
    {
      id: 2,
      name: "Mechanische afzuigventilatie",
      description: "Mechanische afzuiging in vochtige ruimtes, toevoer via roosters in gevels.",
      suitability: "Geschikt voor de meeste bestaande woningen",
      attention: "Kan tot warmteverlies leiden, overweeg WTW-unit",
      icon: "Fan",
      color: "from-purple-500/10 to-pink-500/10",
      image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=400&h=300&fit=crop",
      alt: "Mechanische ventilatie unit geïnstalleerd in badkamer"
    },
    {
      id: 3,
      name: "Gebalanceerde ventilatie met WTW",
      description: "Mechanische toe- en afvoer met warmteterugwinning. Meest efficiënt systeem.",
      suitability: "Ideaal voor nieuwbouw en goed geïsoleerde woningen",
      attention: "Hogere investering, maar laagste energiekosten",
      icon: "Repeat",
      color: "from-green-500/10 to-emerald-500/10",
      image: "https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=400&h=300&fit=crop",
      alt: "WTW ventilatie unit met warmteterugwinning in technische ruimte"
    },
    {
      id: 4,
      name: "Decentrale ventilatie",
      description: "Ventilatie per ruimte met individuele units. Flexibel en eenvoudig te installeren.",
      suitability: "Geschikt voor renovatie en specifieke ruimtes",
      attention: "Meerdere units nodig, onderhoud per unit",
      icon: "Grid",
      color: "from-orange-500/10 to-red-500/10",
      image: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop",
      alt: "Decentrale ventilatie unit gemonteerd in gevel van woning"
    }
  ];

  const healthBenefits = [
    {
      id: 1,
      title: "Betere luchtkwaliteit",
      description: "Verwijdert CO2, vocht en verontreinigende stoffen voor gezondere lucht",
      icon: "Heart",
      color: "text-error"
    },
    {
      id: 2,
      title: "Voorkomt schimmel",
      description: "Constante luchtverversing voorkomt vochtproblemen en schimmelvorming",
      icon: "Shield",
      color: "text-success"
    },
    {
      id: 3,
      title: "Minder allergenen",
      description: "Filters verwijderen pollen, stof en andere allergenen uit de buitenlucht",
      icon: "Leaf",
      color: "text-warning"
    },
    {
      id: 4,
      title: "Beter slapen",
      description: "Frisse lucht en optimale luchtvochtigheid zorgen voor betere nachtrust",
      icon: "Moon",
      color: "text-primary"
    }
  ];

  const costData = [
    { type: 'Natuurlijk', min: 200, max: 500, avg: 350 },
    { type: 'Mechanisch afzuig', min: 1500, max: 3000, avg: 2250 },
    { type: 'WTW systeem', min: 4000, max: 8000, avg: 6000 },
    { type: 'Decentraal', min: 800, max: 2000, avg: 1400 }
  ];

  const providers = [
    {
      id: 1,
      name: "VentilatiePro",
      rating: 4.8,
      reviews: 342,
      specialization: "WTW-systemen en installatie",
      region: "Landelijk",
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=100&h=100&fit=crop",
      alt: "VentilatiePro bedrijfspand met moderne ventilatiesystemen"
    },
    {
      id: 2,
      name: "Frisse Lucht Installaties",
      rating: 4.7,
      reviews: 289,
      specialization: "Complete ventilatie-oplossingen",
      region: "Randstad",
      image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=100&h=100&fit=crop",
      alt: "Frisse Lucht Installaties kantoor met ventilatieproducten"
    },
    {
      id: 3,
      name: "EcoVent Solutions",
      rating: 4.9,
      reviews: 421,
      specialization: "Energiezuinige ventilatiesystemen",
      region: "Landelijk",
      image: "https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=100&h=100&fit=crop",
      alt: "EcoVent Solutions team bij ventilatie-installatie"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      {/* Main Content with proper spacing for fixed navigation */}
      <main className="pt-16 lg:pt-0 lg:ml-[72px] transition-all duration-300">
      <FeaturedBrandSection {...featuredBrandData} onAddToCompare={handleAddToCompare} />
      {/* Hero Section */}
      <motion.section 
        className="relative bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 overflow-hidden py-16 lg:py-24"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div variants={itemVariants}>
              <div className="inline-flex items-center space-x-2 bg-cyan-100 px-4 py-2 rounded-full mb-6">
                <Icon name="Wind" size={20} className="text-cyan-600" />
                <span className="text-sm font-medium text-cyan-800">Ventilatie</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
                Ventilatie: gezonde lucht in huis, zonder warmteverlies
              </h1>
              <p className="text-lg text-muted-foreground mb-6">
                Goede ventilatie is essentieel voor een gezond binnenklimaat. Moderne ventilatiesystemen zorgen voor frisse lucht zonder onnodige warmteverliezen, wat bijdraagt aan uw comfort en gezondheid.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/quote-request-form">
                  <Button size="lg" iconName="FileText" iconPosition="left">
                    Offerte aanvragen
                  </Button>
                </Link>
                <Button variant="outline" size="lg" iconName="Info" iconPosition="left">
                  Meer informatie
                </Button>
              </div>
            </motion.div>

            <motion.div variants={itemVariants} className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-elevation-3">
                <Image
                  src="https://images.unsplash.com/photo-1621905251918-48416bd8575a?w=800&h=600&fit=crop"
                  alt="Moderne WTW ventilatie unit met luchtstromen gevisualiseerd in woning"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">Warmteterugwinning</p>
                        <p className="text-2xl font-bold text-success">Tot 95%</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Luchtkwaliteit</p>
                        <p className="text-2xl font-bold text-primary">Optimaal</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
      {/* Why Ventilation is Essential */}
      <motion.section 
        className="py-16 lg:py-20 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Waarom ventilatie essentieel is
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Goede ventilatie is cruciaal voor een gezond en comfortabel binnenklimaat
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {healthBenefits?.map((benefit) => (
              <motion.div
                key={benefit?.id}
                variants={itemVariants}
                className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-100 hover:shadow-elevation-2 transition-shadow"
              >
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${benefit?.color === 'text-error' ? 'from-red-100 to-pink-100' : benefit?.color === 'text-success' ? 'from-green-100 to-emerald-100' : benefit?.color === 'text-warning' ? 'from-yellow-100 to-orange-100' : 'from-blue-100 to-cyan-100'} flex items-center justify-center mb-4`}>
                  <Icon name={benefit?.icon} size={24} className={benefit?.color} />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit?.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {benefit?.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={itemVariants} className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-8 border border-cyan-100">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-4">
                  Ventilatie & isolatie: de balans
                </h3>
                <p className="text-muted-foreground mb-4">
                  Goed geïsoleerde woningen zijn luchtdichter, waardoor mechanische ventilatie essentieel wordt. Zonder goede ventilatie kunnen vochtproblemen, schimmel en een ongezond binnenklimaat ontstaan.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">Gecontroleerde luchtverversing zonder warmteverlies</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">Voorkomt condensatie en vochtproblemen</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">Optimaal comfort in alle seizoenen</span>
                  </li>
                </ul>
              </div>
              <div className="relative">
                <Image
                  src="https://images.unsplash.com/photo-1632778149955-e80f8ceca2e8?w=600&h=400&fit=crop"
                  alt="Infographic toont luchtstromen in goed geïsoleerde woning met mechanische ventilatie"
                  className="rounded-lg shadow-elevation-2"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>
      {/* Ventilation Systems */}
      <motion.section 
        className="py-16 lg:py-20 bg-gray-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Verschillende ventilatiesystemen
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Kies het systeem dat het beste bij uw woning en situatie past
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {ventilationSystems?.map((system) => (
              <motion.div
                key={system?.id}
                variants={itemVariants}
                className="bg-white rounded-xl overflow-hidden shadow-elevation-2 hover:shadow-elevation-3 transition-shadow"
              >
                <div className="relative h-48 overflow-hidden">
                  <Image
                    src={system?.image}
                    alt={system?.alt}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute top-4 right-4 w-12 h-12 rounded-full bg-gradient-to-br ${system?.color} backdrop-blur-sm flex items-center justify-center border border-white/20`}>
                    <Icon name={system?.icon} size={24} className="text-foreground" />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-heading font-semibold text-foreground mb-3">
                    {system?.name}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {system?.description}
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-2">
                      <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">Geschikt voor:</span> {system?.suitability}
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <Icon name="AlertCircle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium text-foreground">Let op:</span> {system?.attention}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
      {/* Costs & Maintenance */}
      <motion.section 
        className="py-16 lg:py-20 bg-white"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Kosten & onderhoud
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Investering en onderhoudskosten per ventilatiesysteem
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div variants={itemVariants}>
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 lg:p-8 border border-blue-100 h-full">
                <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
                  Indicatieve kosten per systeem
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={costData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                    <Bar dataKey="avg" fill="#06b6d4" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 lg:p-8 border border-purple-100 h-full">
                <h3 className="text-xl font-heading font-semibold text-foreground mb-6">
                  Onderhoud & levensduur
                </h3>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground">Filters vervangen</h4>
                      <span className="text-sm text-primary font-medium">2x per jaar</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Essentieel voor goede luchtkwaliteit en efficiëntie</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground">Systeem reinigen</h4>
                      <span className="text-sm text-primary font-medium">1x per jaar</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Voorkomt vervuiling en bacteriegroei</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground">Levensduur systeem</h4>
                      <span className="text-sm text-success font-medium">15-20 jaar</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Bij goed onderhoud en kwaliteitssysteem</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-foreground">Jaarlijkse kosten</h4>
                      <span className="text-sm text-warning font-medium">€100-€300</span>
                    </div>
                    <p className="text-sm text-muted-foreground">Filters, reiniging en kleine reparaties</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
      {/* Providers & Reviews */}
      <motion.section 
        className="py-16 lg:py-20 bg-gray-50"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={containerVariants}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Aanbieders & ervaringen
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Gecertificeerde installateurs met bewezen expertise
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {providers?.map((provider) => (
              <motion.div
                key={provider?.id}
                variants={itemVariants}
                className="bg-white rounded-xl p-6 shadow-elevation-2 hover:shadow-elevation-3 transition-shadow"
              >
                <div className="flex items-start space-x-4 mb-4">
                  <Image
                    src={provider?.image}
                    alt={provider?.alt}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold text-foreground mb-1">
                      {provider?.name}
                    </h3>
                    <div className="flex items-center space-x-1 mb-1">
                      <Icon name="Star" size={16} className="text-warning fill-current" />
                      <span className="text-sm font-medium text-foreground">{provider?.rating}</span>
                      <span className="text-sm text-muted-foreground">({provider?.reviews} reviews)</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  <span className="font-medium text-foreground">Specialisatie:</span> {provider?.specialization}
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  <span className="font-medium text-foreground">Regio:</span> {provider?.region}
                </p>
                <Link to="/leveranciers">
                  <Button variant="outline" size="sm" className="w-full" iconName="ArrowRight" iconPosition="right">
                    Bekijk profiel
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>

          <motion.div variants={itemVariants} className="text-center">
            <Link to="/quote-request-form">
              <Button size="lg" iconName="FileText" iconPosition="left">
                Offerte aanvragen bij meerdere aanbieders
              </Button>
            </Link>
          </motion.div>
        </div>
      </motion.section>
      <FooterSection />
      </main>
      {/* Floating Compare Button */}
      <AnimatePresence>
        {comparisonProducts?.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed bottom-6 right-6 z-40"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsComparisonOpen(true)}
              className="bg-primary text-white px-6 py-4 rounded-full shadow-2xl flex items-center space-x-3 hover:bg-primary/90 transition-colors"
            >
              <Icon name="GitCompare" size={24} />
              <span className="font-semibold">Vergelijk ({comparisonProducts?.length})</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
      {/* Comparison Modal */}
      <ComparisonModal
        isOpen={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        products={comparisonProducts}
        category="ventilatie"
      />
    </div>
  );
};

export default VentilationCategoryPage;